import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Building2,
  ArrowLeft,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Briefcase,
  FileText,
  Award,
  Download,
  Eye,
  X,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CabinetDetailPage() {
  const { id } = useParams();
  const [cabinet, setCabinet] = useState(null);
  const [enterprises, setEnterprises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdfPreviewOpen, setPdfPreviewOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loadingPdf, setLoadingPdf] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cabinetRes, enterprisesRes] = await Promise.all([
          axios.get(`${API_URL}/cabinets/${id}`),
          axios.get(`${API_URL}/enterprises`),
        ]);
        setCabinet(cabinetRes.data);
        // Filter enterprises for this cabinet
        const cabinetEnterprises = enterprisesRes.data.filter(
          (e) => e.cabinet_id === id
        );
        setEnterprises(cabinetEnterprises);
      } catch (error) {
        console.error(/* PROD */ "Failed to fetch cabinet:", error);
        toast.error("Erreur lors du chargement");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  const getStatusBadge = (status) => {
    const badges = {
      actif: "badge-success",
      suspendu: "badge-warning",
      revoque: "badge-error",
    };
    const labels = {
      actif: "Actif",
      suspendu: "Suspendu",
      revoque: "Révoqué",
    };
    return <span className={badges[status] || "badge-neutral"}>{labels[status] || status}</span>;
  };

  // Fonction pour charger l'aperçu du PDF d'habilitation
  const loadPdfPreview = async () => {
    setLoadingPdf(true);
    try {
      const response = await axios.get(`${API_URL}/cabinets/${id}/habilitation-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      setPdfUrl(url);
      setPdfPreviewOpen(true);
    } catch (error) {
      console.error(/* PROD */ "Erreur chargement PDF:", error);
      toast.error(error.response?.data?.detail || "Erreur lors du chargement du PDF");
    } finally {
      setLoadingPdf(false);
    }
  };

  // Fonction pour télécharger le PDF d'habilitation
  const downloadHabilitationPdf = async () => {
    try {
      toast.info("Génération du PDF en cours...");
      const response = await axios.get(`${API_URL}/cabinets/${id}/habilitation-pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Habilitation_${cabinet?.nom?.replace(/\s+/g, '_')}_${cabinet?.id_igt || 'N-A'}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF téléchargé avec succès");
    } catch (error) {
      console.error(/* PROD */ "Erreur téléchargement PDF:", error);
      toast.error(error.response?.data?.detail || "Erreur lors du téléchargement");
    }
  };

  // Cleanup PDF URL when modal closes
  const handleClosePreview = () => {
    setPdfPreviewOpen(false);
    if (pdfUrl) {
      window.URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Card>
          <CardContent className="p-6">
            <Skeleton className="h-40 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!cabinet) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Cabinet non trouvé</p>
        <Link to="/cabinets">
          <Button className="mt-4">Retour aux cabinets</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-16" data-testid="cabinet-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link to="/cabinets">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="w-5 h-5" />
          </Button>
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-2xl font-bold text-slate-900">{cabinet.nom}</h1>
            {getStatusBadge(cabinet.status)}
          </div>
          <p className="text-muted-foreground mt-1">
            {cabinet.secteur_activite || "Cabinet habilité"} • Détails du cabinet
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Info Card */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-heading">Informations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {cabinet.secteur_activite && (
                <div className="flex items-start gap-3 md:col-span-2">
                  <Briefcase className="w-5 h-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-muted-foreground">Secteur d'activité</p>
                    <p className="font-medium">{cabinet.secteur_activite}</p>
                  </div>
                </div>
              )}
              <div className="flex items-start gap-3">
                <Mail className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Email</p>
                  <p className="font-medium">{cabinet.email}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Téléphone</p>
                  <p className="font-medium">{cabinet.telephone}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Région</p>
                  <p className="font-medium">{cabinet.region || "—"}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Adresse</p>
                  <p className="font-medium">{cabinet.adresse}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Calendar className="w-5 h-5 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-sm text-muted-foreground">Date d'habilitation</p>
                  <p className="font-medium">
                    {cabinet.habilitation_date
                      ? new Date(cabinet.habilitation_date).toLocaleDateString("fr-FR")
                      : "N/A"}
                  </p>
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="font-semibold mb-4">Identifiants légaux</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <p className="text-sm text-primary/70">ID IGT</p>
                  <p className="font-mono font-bold text-primary text-lg">{cabinet.id_igt || "—"}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-muted-foreground">RCCM</p>
                  <p className="font-mono font-medium">{cabinet.rccm}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-muted-foreground">NIF</p>
                  <p className="font-mono font-medium">{cabinet.nif}</p>
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <h3 className="font-semibold mb-4">Responsable</h3>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="font-medium">
                    {cabinet.responsable_prenom} {cabinet.responsable_nom}
                  </p>
                  <p className="text-sm text-muted-foreground">Responsable du cabinet</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats & Habilitation Card */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading">Statistiques</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Briefcase className="w-5 h-5 text-slate-600" />
                <span className="text-sm">Entreprises</span>
              </div>
              <span className="font-bold text-lg">{cabinet.entreprises_count}</span>
            </div>
          </CardContent>

          {/* Section Décision d'Habilitation - visible uniquement pour les cabinets actifs */}
          {cabinet.status === "actif" && (
            <>
              <div className="border-t mx-6"></div>
              <CardHeader className="pt-4 pb-2">
                <CardTitle className="font-heading text-base flex items-center gap-2">
                  <Award className="w-5 h-5 text-emerald-600" />
                  Décision d'Habilitation
                </CardTitle>
                <CardDescription>
                  Document officiel de l'IGT certifiant l'habilitation du cabinet
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-medium text-emerald-900">PDF Officiel</p>
                      <p className="text-xs text-emerald-700">Réf: HAB-{cabinet.id_igt || "N/A"}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 border-emerald-300 text-emerald-700 hover:bg-emerald-100"
                      onClick={loadPdfPreview}
                      disabled={loadingPdf}
                      data-testid="preview-habilitation-btn"
                    >
                      {loadingPdf ? (
                        <div className="w-4 h-4 mr-2 border-2 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <Eye className="w-4 h-4 mr-2" />
                      )}
                      Aperçu
                    </Button>
                    <Button
                      size="sm"
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                      onClick={downloadHabilitationPdf}
                      data-testid="download-habilitation-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger
                    </Button>
                  </div>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </div>

      {/* Enterprises */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading">Entreprises Gérées</CardTitle>
          <CardDescription>Liste des entreprises sous la gestion de ce cabinet</CardDescription>
        </CardHeader>
        <CardContent>
          {enterprises.length === 0 ? (
            <div className="text-center py-8">
              <Briefcase className="w-12 h-12 mx-auto mb-3 text-slate-300" />
              <p className="text-muted-foreground">Aucune entreprise enregistrée</p>
            </div>
          ) : (
            <div className="space-y-3">
              {enterprises.map((enterprise) => (
                <Link
                  key={enterprise.id}
                  to={`/entreprises/${enterprise.id}`}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div>
                    <p className="font-medium text-slate-900">{enterprise.raison_sociale}</p>
                    <p className="text-sm text-slate-500">
                      {enterprise.secteur_activite} • {enterprise.region}
                    </p>
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {enterprise.effectif_global} employés
                  </span>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal Aperçu PDF Habilitation */}
      <Dialog open={pdfPreviewOpen} onOpenChange={handleClosePreview}>
        <DialogContent className="max-w-4xl h-[85vh] p-0 overflow-hidden">
          <DialogHeader className="p-4 pb-2 border-b">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="flex items-center gap-2">
                  <Award className="w-5 h-5 text-emerald-600" />
                  Décision d'Habilitation - {cabinet?.nom}
                </DialogTitle>
                <DialogDescription>
                  Réf: HAB-{cabinet?.id_igt || "N/A"} • Aperçu du document officiel
                </DialogDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  onClick={downloadHabilitationPdf}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger
                </Button>
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 h-full bg-slate-100">
            {pdfUrl ? (
              <iframe
                src={pdfUrl}
                className="w-full h-[calc(85vh-80px)]"
                title="Aperçu PDF Habilitation"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-4 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-muted-foreground">Chargement du PDF...</p>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
